
import React, { useState, useEffect } from 'react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { StudentInsightsPanel } from './components/StudentInsightsPanel';
import { StudentListSection } from './components/StudentListSection';
import { useStudentFilters } from './hooks/useStudentFilters';
import { devLog } from '@/utils/devLog';
import { Profile } from '@/types/profile';
import { toast } from 'sonner';
import { fetchStudentKpi } from '@/services/studentKpiService';

const StudentsPage: React.FC = () => {
  console.log("🔍 StudentsPage component rendering");
  
  const { profile, isLoading: profileLoading, isError: profileError } = useUserProfile() as { 
    profile: Profile | null, 
    isLoading: boolean,
    isError: boolean
  };
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const { filters, setFilters } = useStudentFilters();
  
  useEffect(() => {
    console.log("🔍 StudentsPage: profile data:", profile);
  }, [profile]);

  useEffect(() => {
    if (profileError) {
      console.error("Error loading profile in StudentsPage");
      toast.error("There was an error loading your profile. Please try again.");
    }
  }, [profileError]);
  
  useEffect(() => {
    if (profile?.id) {
      console.log("⏳ Checking KPI fetching for creator:", profile.id);
      fetchStudentKpi(profile.id, 30)
        .then(result => console.log("🔍 KPI test result:", result))
        .catch(err => console.error("❌ KPI test error:", err));
    }
  }, [profile?.id]);
  
  const openStudentDrawer = (student: any) => {
    setSelectedStudent(student);
    setShowDrawer(true);
  };
  
  const closeDrawer = () => {
    setShowDrawer(false);
    setTimeout(() => setSelectedStudent(null), 300);
  };
  
  const isProfileWithId = (profile: Profile | null): profile is Profile & { id: string } => {
    return !!profile && 'id' in profile && typeof profile.id === 'string';
  };
  
  useEffect(() => {
    if (!profileLoading && profile) {
      if (isProfileWithId(profile)) {
        devLog('[StudentsPage] Creator profile loaded, id:', profile.id);
        console.log('[StudentsPage] Creator profile loaded with ID:', profile.id);
        setFilters(prev => ({ ...prev, creatorId: profile.id }));
      } else {
        console.error('[StudentsPage] Profile loaded but missing ID:', profile);
        toast.error("Your profile data is incomplete. Please try refreshing the page.");
      }
    }
  }, [profile, profileLoading, setFilters]);

  if (profileLoading) {
    console.log('[StudentsPage] Loading profile...');
    return <div className="container py-6">Loading creator profile...</div>;
  }

  if (!profile) {
    console.log('[StudentsPage] No profile data found');
    return <div className="container py-6">Unable to load creator profile</div>;
  }

  if (!isProfileWithId(profile)) {
    console.log('[StudentsPage] Invalid profile data:', profile);
    return <div className="container py-6">Invalid profile data</div>;
  }

  console.log('[StudentsPage] Rendering with creator ID:', profile.id);

  return (
    <div className="container py-6 space-y-8">
      <div className="flex flex-col space-y-2">
        <h4 className="text-2xl font-bold tracking-tight">Insights</h4>
        <p className="text-muted-foreground">
          View and manage your students across all courses
        </p>
      </div>

      <StudentInsightsPanel creatorId={profile.id} />

      <StudentListSection 
        creatorId={profile.id}
        onViewStudent={openStudentDrawer}
        filters={filters}
        setFilters={setFilters}
      />
    </div>
  );
};

export default StudentsPage;
